import {
  FileSignature,
  MessageCircle,
  MapPin,
  Users,
  Receipt,
  BookOpen,
  LayoutDashboard,
  Building2,
  FolderLock,
  ArrowUpRight,
} from "lucide-react";

const features = [
  {
    Icon: FileSignature,
    title: "Digital Enrollment & E-Sign",
    body: "Every Ohio enrollment form pre-built. Parents sign on their phone, documents file themselves.",
    span: "md:col-span-7",
    tone: "emerald",
  },
  {
    Icon: MessageCircle,
    title: "Parent App",
    body: "Daily reports, photos, messages — one quiet feed for families.",
    span: "md:col-span-5",
    tone: "orange",
  },
  {
    Icon: MapPin,
    title: "Staff App · GPS Clock-in",
    body: "Geo-fenced clock in/out. Timesheets ready for payroll.",
    span: "md:col-span-4",
    tone: "stone",
  },
  {
    Icon: Users,
    title: "Ratios, Attendance & Classrooms",
    body: "Live OAC 5101:2-12-18 ratio monitoring. Alerts before you ever slip out of compliance.",
    span: "md:col-span-8",
    tone: "emerald",
  },
  {
    Icon: FolderLock,
    title: "Licensing File",
    body: "Upload Fire Drills, Health Inspection, Food Service, Occupancy, Insurance — every operating document with live expiry alerts.",
    span: "md:col-span-6",
    tone: "emerald",
  },
  {
    Icon: Receipt,
    title: "Billing & Automated Payments",
    body: "Recurring tuition, late fees, subsidy reconciliation — ACH + card via Stripe.",
    span: "md:col-span-6",
    tone: "orange",
  },
  {
    Icon: BookOpen,
    title: "Lesson Plans & Curriculum",
    body: "Aligned to Ohio Early Learning & Development Standards (ELDS).",
    span: "md:col-span-5",
    tone: "stone",
  },
  {
    Icon: LayoutDashboard,
    title: "Real-time Center Dashboard",
    body: "One calm glance at ratios, attendance, revenue, and staffing.",
    span: "md:col-span-7",
    tone: "emerald",
  },
  {
    Icon: Building2,
    title: "Multi-location",
    body: "Roll-up reporting for 2 to 200 centers across Ohio.",
    span: "md:col-span-12",
    tone: "orange",
  },
];

const toneStyles = {
  emerald: "bg-white border-stone-200 text-stone-900",
  orange: "bg-white border-stone-200 text-stone-900",
  stone: "bg-stone-900 border-stone-800 text-stone-50",
};

const iconTone = {
  emerald: "bg-emerald-700/10 text-emerald-700",
  orange: "bg-orange-500/10 text-orange-600",
  stone: "bg-emerald-400/15 text-emerald-300",
};

export const Features = () => {
  return (
    <section id="features" data-testid="features-section" className="py-24 sm:py-32 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold text-emerald-700">
            Everything a center needs
          </div>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl font-medium text-stone-900 tracking-tight">
            One calm operating system.
          </h2>
          <p className="mt-4 text-lg text-stone-600 max-w-xl">
            Replace the spreadsheet mess, paper binders, and four different apps with one
            Ohio-native platform.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-5">
          {features.map((f, i) => (
            <div
              key={f.title}
              data-testid={`feature-card-${i}`}
              className={`${f.span} ${toneStyles[f.tone]} group rounded-2xl p-7 border shadow-soft hover:shadow-lift hover:-translate-y-1 transition-all duration-300 relative overflow-hidden`}
            >
              <div className={`w-11 h-11 rounded-xl grid place-items-center ${iconTone[f.tone]}`}>
                <f.Icon className="w-5 h-5" />
              </div>
              <h3 className={`mt-5 font-display text-xl font-medium ${f.tone === "stone" ? "text-stone-50" : "text-stone-900"}`}>
                {f.title}
              </h3>
              <p className={`mt-2 text-sm leading-relaxed ${f.tone === "stone" ? "text-stone-300" : "text-stone-600"}`}>
                {f.body}
              </p>
              <ArrowUpRight
                className={`absolute top-7 right-7 w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity ${
                  f.tone === "stone" ? "text-stone-300" : "text-stone-400"
                }`}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
